import type Department from '../../offices/models/department/department';
import type RoomObsolete from '../../offices/models/room/room-obsolete';

/**
 * The rooms to offer when `selectedDepartment` is picked.
 *
 * A room that belongs to **no** department is a *shared* room: it is offered under every
 * department, alongside the rooms actually linked to the selected one. This is the same rule the
 * Flutter app applies in `Office.getRoomsByDepartment` — the two clients must not drift, because
 * they write to the same booking table and the server now rejects a pair that neither allows
 * (`Room.AcceptsDepartment`).
 *
 * Extracted from `day-part-group.component.tsx` so the rule has a name and a test rather than
 * living as a closure inside a render.
 *
 * Two behaviours worth stating, both deliberate:
 *
 * - **No department selected yet** returns the shared rooms rather than nothing. The booking modal
 *   opens in exactly that state for a new agenda entry, and returning `[]` meant an administrator
 *   in a department-using office saw an empty room list with nothing explaining why. Shared rooms
 *   are valid whichever department is picked next, so offering them is never wrong.
 * - **An office with no departments** returns everything untouched.
 */
const roomsForDepartment = (
  rooms: RoomObsolete[] | undefined,
  departments: Department[],
  selectedDepartment: Department | undefined,
): RoomObsolete[] | undefined => {
  if (departments.length === 0) {
    return rooms;
  }

  if (selectedDepartment == null) {
    return rooms?.filter((room) => room.departments.length === 0);
  }

  // A `filter` rather than the `forEach`-and-push this replaced: a room linked to several
  // departments used to be pushed once per match and appear in the list more than once.
  return rooms?.filter(
    (room) =>
      room.departments.length === 0 ||
      room.departments.some((department) => department.id === selectedDepartment.id),
  );
};

export default roomsForDepartment;
